import { FormEvent } from 'react';
import { FetchRemotePage } from 'reusable/RemoteSearch/remotePage';
import { SearchSelectOption } from 'reusable/RemoteSearch/SearchSelect';

export const sortingOptions = (t: Function) => [
  { label: t('sort.default', { ns: 'common' }), value: '' },
  { label: t('sort.device-id', { ns: 'common' }), value: 'device_id' },
];

export interface SelectOption {
  label: string;
  id: string;
  value: string;
}

export enum DialogType {
  Add = 'Add',
  Edit = 'Edit',
  Delete = 'Delete',
}

export interface DialogConfig {
  title: string;
  subtitle?: string;
  note?: string;
  fields: DialogField[];
}

export interface DialogField {
  label: string;
  type?: string;
  name: string;
  value?: string;
  isRequired?: boolean;
  readOnly?: boolean;
  options?: SelectOption[];
  /**
   * EV-116（照合の懸念4）。日付・時刻の組の時刻の欄の、元の時刻（UTC の ISO 文字列）。編集の画面は、
   * 元の値を変えていなければ元の時刻で前後を比べ、2回ある時刻の案内を出さない。
   */
  originalIso?: string | null;
  /**
   * EV-322（ADM-15）。この欄の選択肢を読めなかったときの失敗。欄の下に既存の FetchFailed の文言と失敗の種類を
   * 注記（role="alert"）で出し、欄と aria-describedby で結ぶ。onRetry で選択肢を読み直す。
   */
  fetchError?: unknown;
  onRetry?: () => void;
  /**
   * EV-402。入力の説明（欄の下に出し、欄と aria-describedby で結ぶ）。説明のある欄は2列分の幅で出す。
   */
  helpText?: string;
  /**
   * EV-493。選ぶ欄（type 'select'）の候補をサーバーで検索する。あれば options の代わりに使い、文字を入れると
   * 候補が出る欄（SearchSelect）にする。fixedOptions は必ず先に出す候補（機器の状態など。追加の画面の初期値は
   * その先頭）。knownLabels は候補に無い値の名前（開いたときの割当先など）。読めなかったときの表示は欄が出す。
   */
  search?: {
    fetchPage: FetchRemotePage<SearchSelectOption>;
    fixedOptions?: SearchSelectOption[];
    knownLabels?: Record<string, string>;
  };
  /**
   * 2026-11-03 UAT A08。この欄を変えたときに、ほかの欄へ入れる値（利用開始日から使用期限を埋めるなど）。
   * previous は変える前の値、current は変える前の入力の全体。返した欄だけを書き換える（手で直した値を残すかは返す側で決める）。
   */
  fillOther?: (value: string, previous: string, current: NewDataField) => Record<string, string>;
  /**
   * A07（2026-11-03 和田様の UAT）。文字の欄（type 'text'）の入力の候補（datalist）。車種に、同じ会社で
   * 過去に入れた車種を出す。候補に無い値もそのまま入れられる。
   */
  suggestions?: string[];
}

/** A07。車両ナンバーを地名・分類番号・ひらがな・一連番号の4つの欄で入れる欄の種類（reusable/PlateNumberInput）。 */
export const PLATE_FIELD_TYPE = 'plate';

export interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  handleSubmit: (
    e: FormEvent<Element>,
    newData: NewDataField & { id: string }
    // EV-461。作成のダイアログ（AddDialog）だけが読む。結果が分からないまま（入力を固定する）なら unresolved: true。
  ) => Promise<void | { unresolved?: boolean }>;
  title: string;
  subtitle: string;
  note?: string;
  fields: DialogField[];
  deviceId?: string;
  attendanceId?: string;
  vehicleId?: string;
  taskSessionId?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** 2026-11-03 UAT A15。削除の確認から、その場で記録を保存（CSV）する。保存した行の数を返す。 */
  exportRecords?: () => Promise<number>;
}

export interface NewDataField {
  id: string;
  [key: string]: string | number | null;
}

export const createTextField = (
  label: string,
  name: string,
  isRequired: boolean,
  value: string,
  readOnly?: boolean
): DialogField => ({
  label,
  type: 'text',
  name,
  isRequired,
  value,
  readOnly,
});

export const createSelectField = (
  label: string,
  name: string,
  isRequired: boolean,
  value: string,
  readOnly?: boolean
): DialogField => ({
  label,
  type: 'select',
  name,
  isRequired,
  value,
  readOnly,
});

export const createDateField = (
  label: string,
  name: string,
  isRequired: boolean,
  value: string,
  readOnly?: boolean
): DialogField => ({
  label,
  type: 'date',
  name,
  isRequired,
  value,
  readOnly,
});

export const createTimeField = (
  label: string,
  name: string,
  isRequired: boolean,
  value: string,
  readOnly?: boolean
): DialogField => ({
  label,
  type: 'time',
  name,
  isRequired,
  value,
  readOnly,
});

/** BE-09-AC5。勤怠・作業の修正の理由の欄の種類（DialogField.type）と、サーバーの上限の文字数。 */
export const REASON_FIELD_TYPE = 'reason';
export const REASON_MAX_LENGTH = 500;
